import React from 'react';
import { Dropdown } from 'antd';
import {
    Barcode,
    MoreVertical,
    Pencil,
    SquareArrowOutUpRight,
    TriangleAlert,
} from 'lucide-react';
import { QuickAdjustPlanogram as Planogram } from '../../types';
import {
    formatCents,
    formatDueDate,
    getCalculatedMargin,
    getDueDateLevel,
    getMarginChipLevel,
    getStatusColorLevel,
    getStatusLabel,
    getSuggestedAppPriceInCents,
    getTargetMargin,
    resolveProductAlert,
} from '../../utils';
import ProductAvatar from '../ProductAvatar';
import * as S from './styles';

type ProductCardProps = {
    planogram: Planogram;
    onEdit: (planogram: Planogram) => void;
    onOpenProduct: (productId: number) => void;
};

export const ProductCard = ({
    planogram,
    onEdit,
    onOpenProduct,
}: ProductCardProps) => {
    const { product } = planogram;
    const ean = product.BarCodes?.[0]?.code || 'Sem código de barras';
    const categoryName = product.Category?.name ?? 'Sem categoria';

    const targetMargin = getTargetMargin(planogram);
    const actualMargin = getCalculatedMargin(
        planogram.price_final_in_cents,
        product.last_price_in_cents,
    );
    const suggestedPriceInCents = getSuggestedAppPriceInCents(
        product.last_price_in_cents,
        targetMargin,
    );

    const productAlert = resolveProductAlert({
        stockAmount: planogram.stock_amount,
        alertLevel: planogram.alert_level,
        dueDate: planogram.due_date,
        priceFinalInCents: planogram.price_final_in_cents,
        suggestedPriceInCents,
    });

    const dueDateLevel = getDueDateLevel(planogram.due_date);
    const marginLevel = getMarginChipLevel(actualMargin, targetMargin);
    const statusLevel = getStatusColorLevel(planogram.status);
    const isStockLow = planogram.stock_amount <= planogram.alert_level;

    const statusLabel = getStatusLabel(planogram.status);

    return (
        <S.Card>
            <S.CardHeader>
                <ProductAvatar product={product} size="large" />
                <S.HeaderInfo>
                    <S.ProductName>{product.name}</S.ProductName>
                </S.HeaderInfo>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            {
                                key: 'open-product',
                                label: 'Abrir na tela de Produtos',
                                icon: <SquareArrowOutUpRight size={15} />,
                                onClick: () => onOpenProduct(product.id),
                            },
                        ],
                    }}
                >
                    <S.MenuButton title="Ações" type="button">
                        <MoreVertical size={18} />
                    </S.MenuButton>
                </Dropdown>
            </S.CardHeader>

            <S.MetaRow>
                <S.EanLabel>
                    <Barcode size={15} strokeWidth={1.7} />
                    {ean}
                </S.EanLabel>
                <S.CategoryLabel>{categoryName}</S.CategoryLabel>
            </S.MetaRow>

            {productAlert && (
                <S.AlertStrip level={productAlert.level}>
                    <TriangleAlert size={14} />
                    {productAlert.text}
                </S.AlertStrip>
            )}

            <S.Divider />

            <S.FieldsGrid>
                <S.FieldCell>
                    <S.FieldLabel>Qtd. padrão</S.FieldLabel>
                    <S.FieldValue>{planogram.standard_amount}</S.FieldValue>
                </S.FieldCell>
                <S.FieldCell>
                    <S.FieldLabel>Em estoque</S.FieldLabel>
                    <S.FieldValue level={isStockLow ? 'danger' : undefined}>
                        {planogram.stock_amount}
                    </S.FieldValue>
                </S.FieldCell>
                <S.FieldCell>
                    <S.FieldLabel>Nível de alerta</S.FieldLabel>
                    <S.FieldValue>{planogram.alert_level}</S.FieldValue>
                </S.FieldCell>
                <S.FieldCell>
                    <S.FieldLabel>Vencimento</S.FieldLabel>
                    <S.FieldValue level={dueDateLevel}>
                        {formatDueDate(planogram.due_date) ?? 'Não informado'}
                    </S.FieldValue>
                </S.FieldCell>
                <S.FieldCell>
                    <S.FieldLabel>Últ. preço de compra un.</S.FieldLabel>
                    <S.FieldValue>
                        {formatCents(product.last_price_in_cents)}
                    </S.FieldValue>
                </S.FieldCell>
                <S.FieldCell>
                    <S.FieldLabel>Cód. planograma</S.FieldLabel>
                    <S.FieldValue>
                        {planogram.planogram_code || '—'}
                    </S.FieldValue>
                </S.FieldCell>
                <S.FieldCell emphasis>
                    <S.FieldLabel>Preço lista</S.FieldLabel>
                    <S.FieldValue strong>
                        {formatCents(planogram.price_list_in_cents)}
                    </S.FieldValue>
                </S.FieldCell>
                <S.FieldCell emphasis>
                    <S.FieldLabel>Preço un. App</S.FieldLabel>
                    <S.AppPriceRow>
                        <S.FieldValue strong accent>
                            {formatCents(planogram.price_final_in_cents)}
                        </S.FieldValue>
                        <S.MarginChip level={marginLevel}>
                            {actualMargin != null
                                ? `${Math.round(actualMargin * 100)}%`
                                : '—'}
                        </S.MarginChip>
                    </S.AppPriceRow>
                    <S.AppPriceCaption>
                        Preço exibido ao cliente
                    </S.AppPriceCaption>
                </S.FieldCell>
            </S.FieldsGrid>

            <S.Footer>
                <div>
                    <S.StatusLabel>Status</S.StatusLabel>
                    <S.StatusChip level={statusLevel}>
                        <S.StatusDot level={statusLevel} />
                        {statusLabel}
                    </S.StatusChip>
                </div>
                <S.EditButton type="button" onClick={() => onEdit(planogram)}>
                    <Pencil size={15} />
                    Editar
                </S.EditButton>
            </S.Footer>
        </S.Card>
    );
};
